import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import useApplicationChemicals from 'hooks/useApplicationChemicals';
import type { ApplicationChemical } from 'hooks/useApplicationChemicals/types';
import useChemical from 'hooks/useChemical';
import type { EnrichedChemical } from 'hooks/useChemical/types';
import type {
    MixRecipe,
    MixRecipeProduct,
    MixRecipeProductCategory,
    MixRecipeProductSignalWord,
    MixRecipeRateUnit
} from 'hooks/useMixRecipe/types';

import MixApplyPage from 'pages/MixApplyPage';

import { TEST_ID } from './constants';
import type { FunctionComponent } from './types';

const CATEGORY_VALUES: readonly MixRecipeProductCategory[] = [
    'adjuvant',
    'fungicide',
    'growth',
    'herbicide',
    'insecticide',
    'surfactant'
];

const SIGNAL_VALUES: readonly MixRecipeProductSignalWord[] = ['caution', 'danger', 'warning'];

const RATE_UNITS: readonly MixRecipeRateUnit[] = ['oz/ac', 'lb/ac', 'gal/ac', 'pt/ac', 'qt/ac'];

const normalizeCategory = (
    raw: string | null | undefined
): MixRecipeProductCategory | undefined => {
    if (!raw) {
        return undefined;
    }

    return CATEGORY_VALUES.find(value => value === raw.toLowerCase());
};

const normalizeSignalWord = (
    raw: string | null | undefined
): MixRecipeProductSignalWord | undefined => {
    if (!raw) {
        return undefined;
    }

    return SIGNAL_VALUES.find(value => value === raw.toLowerCase());
};

const normalizeRateUnit = (rateUnit: string, ratePerUnit: string): MixRecipeRateUnit => {
    const key = `${rateUnit}/${ratePerUnit === 'acre' ? 'ac' : ratePerUnit}`.toLowerCase();

    return RATE_UNITS.find(value => value === key) ?? 'oz/ac';
};

const buildProduct = (
    chemical: ApplicationChemical,
    enriched: EnrichedChemical | null
): MixRecipeProduct => ({
    category: normalizeCategory(chemical.classification),
    epaRegNumber: chemical.epaNumber ?? '',
    formulation: enriched?.formulationName ?? undefined,
    isRestrictedUse: enriched?.isRestrictedUse ?? undefined,
    labelRateRange:
        enriched && enriched.genericMinRate !== null && enriched.genericMaxRate !== null
            ? { maximum: enriched.genericMaxRate, minimum: enriched.genericMinRate }
            : undefined,
    manufacturer: enriched?.manufacturer ?? undefined,
    modeOfActionCode: enriched?.modeOfActions?.[0] ?? undefined,
    productId: chemical.chemicalId,
    productName: chemical.labelName,
    rate: chemical.applicationRate,
    rateUnit: normalizeRateUnit(chemical.rateUnit, chemical.ratePerUnit),
    signalWord: normalizeSignalWord(enriched?.signalWord)
});

const buildRecipe = async (
    applicationId: string,
    chemicals: ApplicationChemical[],
    fetchChemical: (params: { id: string }) => Promise<EnrichedChemical | null>
): Promise<MixRecipe> => {
    const enriched = await Promise.all(
        chemicals.map(chemical =>
            chemical.chemicalType === 'ENRICHED'
                ? fetchChemical({ id: chemical.chemicalId })
                : Promise.resolve(null)
        )
    );

    return {
        mixRecipeId: applicationId,
        name: 'Application Mix',
        products: chemicals.map((chemical, index) => buildProduct(chemical, enriched[index]))
    };
};

const ApplyFromApplicationRoute: FunctionComponent = ({ testId = TEST_ID }) => {
    const { applicationId } = useParams<{ applicationId: string }>();
    const { fetchApplicationChemicals } = useApplicationChemicals();
    const { fetchChemical } = useChemical();
    const [recipe, setRecipe] = useState<MixRecipe | null>(null);

    useEffect(() => {
        if (!applicationId) {
            return;
        }

        let cancelled = false;

        (async () => {
            const chemicals = await fetchApplicationChemicals({ applicationId });

            if (cancelled || !chemicals) {
                return;
            }

            const built = await buildRecipe(applicationId, chemicals, fetchChemical);

            if (!cancelled) {
                setRecipe(built);
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId]);

    return <MixApplyPage overrideMixRecipe={recipe ?? undefined} testId={testId} />;
};

export default ApplyFromApplicationRoute;
